import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'chart.js/auto';
import { createPortal } from 'react-dom';
import { truncateString } from '../../helper';

function HorizontalPercentageBarChart(props) {
  const { labels, data, images, Tooltip, extraDatasets = [] } = props;
  const chartRef = useRef();
  const canvasRef = useRef(null);
  const [tooltipModel, setTooltipModel] = useState();
  const dataPoint = tooltipModel?.dataPoints[0];
  const image = dataPoint && images?.[dataPoint.dataIndex];
  const height = labels.length * 40 + 50;

  useEffect(() => {
    if (!canvasRef.current) return;

    chartRef.current?.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: {
        labels: labels.map((label) => truncateString(label, 60)),
        datasets: [
          {
            data,
            label: 'Percentage',
            backgroundColor: '#3588d1',
            borderWidth: 0,
            barThickness: 24,
          },
          ...extraDatasets,
          {
            data: new Array(data.length).fill(100),
            backgroundColor: '#f7f7f7',
            hoverBackgroundColor: '#f7f7f7',
            borderWidth: 0,
            barThickness: 24,
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        plugins: {
          legend: { display: false },
          tooltip: {
            mode: 'index',
            position: 'middle',
            filter: ({ datasetIndex }) => datasetIndex === 0,
            ...(Tooltip && {
              enabled: false,
              external: (context) => setTooltipModel({ ...context.tooltip }),
            }),
          },
        },
        scales: {
          y: {
            grid: { display: false },
            ticks: { padding: 10 },
            stacked: true,
          },
          x: {
            min: 0,
            max: 100,
            grid: { drawTicks: false },
            border: { display: false },
            ticks: {
              padding: 14,
              autoSkip: false,
              stepSize: 25,
              count: 5,
              // eslint-disable-next-line id-denylist
              callback: (tickValue) => `${tickValue}%`,
            },
          },
        },
      },
    });
  }, [data]);

  return (
    <>
      <div style={{ height: `${height}px` }}>
        <canvas className="engage-report-question-charts__canvas" ref={canvasRef} />
      </div>
      {Tooltip
        ? createPortal(
            <Tooltip tooltipModel={tooltipModel} canvasRef={canvasRef} image={image} />,
            document.body
          )
        : null}
    </>
  );
}

HorizontalPercentageBarChart.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  data: PropTypes.arrayOf(PropTypes.number).isRequired,
  extraDatasets: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.shape({}))),
  images: PropTypes.arrayOf(PropTypes.string),
  Tooltip: PropTypes.func,
};

HorizontalPercentageBarChart.defaultProps = {
  images: undefined,
  Tooltip: undefined,
  extraDatasets: undefined,
};

export default HorizontalPercentageBarChart;
